import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { workCalendarApi } from "../../services/workCalendarApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const CLASSIFICATIONS = ["REGULAR_WORKDAY", "COMPANY_HOLIDAY", "STATUTORY_HOLIDAY", "NON_STATUTORY_HOLIDAY", "STORE_CLOSURE", "SPECIAL_OPERATING_DAY", "REVIEW_REQUIRED"];

export default function WorkCalendarDetailPage() {
  const { calendarId } = useParams();
  const { t } = useLocalization();
  const { canViewWorkCalendar, canManageWorkCalendar, canApproveWorkCalendar } = usePermissions();
  const [calendar, setCalendar] = useState(null);
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [days, setDays] = useState([]);
  const [newVersion, setNewVersion] = useState({ effective_from: "", source_document: "" });
  const [importText, setImportText] = useState("");
  const [classifyForm, setClassifyForm] = useState({ date: "", classification: "REVIEW_REQUIRED", notes: "" });

  const load = useCallback(() => {
    workCalendarApi.getCalendar(calendarId).then((r) => setCalendar(r.data)).catch(() => {});
    workCalendarApi.listVersions(calendarId).then((r) => {
      setVersions(r.data);
      if (!activeVersionId && r.data.length > 0) setActiveVersionId(r.data[0].id);
    }).catch(() => {});
  }, [calendarId, activeVersionId]);

  useEffect(() => { if (canViewWorkCalendar) load(); }, [canViewWorkCalendar, load]);
  useEffect(() => {
    if (activeVersionId) workCalendarApi.listDays(activeVersionId).then((r) => setDays(r.data)).catch(() => {});
  }, [activeVersionId]);

  if (!canViewWorkCalendar) return <p className="text-sm text-gray-500" data-testid="work-calendar-detail-access-denied">{t("common.accessDenied")}</p>;

  const activeVersion = versions.find((v) => v.id === activeVersionId);

  const createVersion = async () => {
    if (!newVersion.effective_from) { toast.error(t("mgmtWorkCalendarDetail.effectiveFromRequired")); return; }
    try {
      const { data } = await workCalendarApi.createVersion(calendarId, newVersion);
      toast.success(t("mgmtWorkCalendarDetail.versionCreated"));
      setNewVersion({ effective_from: "", source_document: "" });
      setActiveVersionId(data.id);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const importDays = async () => {
    const dates = importText.split(/[\n,]/).map((d) => d.trim()).filter(Boolean);
    if (dates.length === 0) { toast.error(t("mgmtWorkCalendarDetail.enterAtLeastOneDate")); return; }
    try {
      const { data } = await workCalendarApi.importDays(activeVersionId, dates);
      toast.success(`${t("mgmtWorkCalendarDetail.imported")} ${data.imported}, ${t("mgmtWorkCalendarDetail.skipped")} ${data.skipped_already_present}`);
      setImportText("");
      workCalendarApi.listDays(activeVersionId).then((r) => setDays(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const classifyDay = async () => {
    if (!classifyForm.date) { toast.error(t("mgmtWorkCalendarDetail.dateRequired")); return; }
    try {
      await workCalendarApi.classifyDay(activeVersionId, classifyForm.date, classifyForm.classification, classifyForm.notes);
      toast.success(t("mgmtWorkCalendarDetail.dayClassified"));
      setClassifyForm({ date: "", classification: "REVIEW_REQUIRED", notes: "" });
      workCalendarApi.listDays(activeVersionId).then((r) => setDays(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitVersion = async () => {
    try { await workCalendarApi.submitVersion(activeVersionId); toast.success(t("mgmtWorkCalendarDetail.submittedForApproval")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const approveVersion = async () => {
    try { await workCalendarApi.approveVersion(activeVersionId); toast.success(t("mgmtWorkCalendarDetail.versionApproved")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="work-calendar-detail-page">
      <PageHelpBanner pageKey="work-calendar-detail" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtWorkCalendarDetail.help")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{calendar?.name || t("mgmtWorkCalendar.title")}</h2>
        <p className="text-sm text-gray-500">{calendar?.scope_type}{calendar?.store_location ? ` - ${calendar.store_location}` : ""}</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {versions.map((v) => (
          <button key={v.id} data-testid={`version-tab-${v.id}`} onClick={() => setActiveVersionId(v.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${activeVersionId === v.id ? "bg-blue-600 text-white border-blue-600" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
            v{v.version} ({v.status})
          </button>
        ))}
      </div>

      {canManageWorkCalendar && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="create-version-panel">
          <input data-testid="version-effective-from-input" type="date" value={newVersion.effective_from} onChange={(e) => setNewVersion({ ...newVersion, effective_from: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          <input data-testid="version-source-doc-input" value={newVersion.source_document} onChange={(e) => setNewVersion({ ...newVersion, source_document: e.target.value })} placeholder={t("mgmtWorkCalendarDetail.sourceDocumentOptional")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm col-span-2" />
          <button data-testid="create-version-btn" onClick={createVersion} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtWorkCalendarDetail.newVersion")}</button>
        </div>
      )}

      {activeVersion && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">{t("mgmtWorkCalendarDetail.version")} {activeVersion.version} - {t("mgmtPayroll.effective")} {activeVersion.effective_from}</p>
            <div className="flex items-center gap-2">
              <StatusBadge status={activeVersion.status} />
              {canManageWorkCalendar && activeVersion.status === "DRAFT" && (
                <button data-testid="submit-version-btn" onClick={submitVersion} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtWorkCalendarDetail.submitForApproval")}</button>
              )}
              {canApproveWorkCalendar && activeVersion.status === "PENDING_APPROVAL" && (
                <button data-testid="approve-version-btn" onClick={approveVersion} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700">{t("common.approve")}</button>
              )}
            </div>
          </div>

          {canManageWorkCalendar && activeVersion.status === "DRAFT" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="import-days-panel">
                <textarea data-testid="import-dates-textarea" value={importText} onChange={(e) => setImportText(e.target.value)} placeholder={t("mgmtWorkCalendarDetail.datesPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm md:col-span-2" rows={2} />
                <button data-testid="import-days-btn" onClick={importDays} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtWorkCalendarDetail.importDates")}</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="classify-day-panel">
                <input data-testid="classify-date-input" type="date" value={classifyForm.date} onChange={(e) => setClassifyForm({ ...classifyForm, date: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
                <select data-testid="classify-classification-select" value={classifyForm.classification} onChange={(e) => setClassifyForm({ ...classifyForm, classification: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
                  {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input data-testid="classify-notes-input" value={classifyForm.notes} onChange={(e) => setClassifyForm({ ...classifyForm, notes: e.target.value })} placeholder={t("mgmtWorkCalendarDetail.notes")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
                <button data-testid="classify-day-btn" onClick={classifyDay} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtWorkCalendarDetail.classifyDay")}</button>
              </div>
            </>
          )}

          <div className="rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("common.date")}</th><th className="px-4 py-3 font-medium">{t("mgmtWorkCalendarDetail.classification")}</th><th className="px-4 py-3 font-medium">{t("mgmtWorkCalendarDetail.source")}</th><th className="px-4 py-3 font-medium">{t("mgmtWorkCalendarDetail.notes")}</th></tr></thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.id} className="border-t border-gray-100" data-testid={`calendar-day-row-${d.date}`}>
                    <td className="px-4 py-2 text-gray-600">{d.date}</td>
                    <td className="px-4 py-2"><StatusBadge status={d.classification} /></td>
                    <td className="px-4 py-2 text-gray-500">{d.source}</td>
                    <td className="px-4 py-2 text-gray-500">{d.notes || "-"}</td>
                  </tr>
                ))}
                {days.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="no-calendar-days-row">{t("mgmtWorkCalendarDetail.noDaysClassified")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {versions.length === 0 && <p className="text-sm text-gray-500" data-testid="no-versions-message">{t("mgmtWorkCalendarDetail.noVersionsYet")}</p>}
    </div>
  );
}
